import React, { useEffect, useMemo, useState } from 'react';
import { t, getCurrentLanguage } from '../../../utils/translations';
import useLanguageStore from '../../../store/languageStore';

/**
 * Elapsed-time indicator shown while a turn is in flight.
 *
 * WHY THIS EXISTS
 *
 * The DTA widget streamed token by token, so the visitor saw progress within a
 * second. This agent returns ONE complete answer at the end of the turn, and turns
 * of 7s, 36s and ~139s have all been measured against a 285s ceiling. A bare
 * typing cursor for two minutes reads as "broken", so the honest substitute is to
 * say how long it has been waiting.
 *
 * WHY IT IS NOT A PROGRESS BAR
 *
 * The backend's `heartbeat` frame carries `elapsed_ms` and nothing else. There is no
 * completion estimate anywhere in the chain, so any percentage would be invented,
 * and an invented bar that sits at 90% for a minute is worse than a number that is
 * merely honest.
 *
 * WHY IT TICKS LOCALLY
 *
 * Heartbeats arrive every 15 seconds. A counter that only advanced on a heartbeat
 * would jump 0 -> 15 -> 30, which looks like a stall between jumps. So the seconds
 * are computed locally from the turn's start, and the heartbeat is used to correct
 * the anchor rather than to drive the display.
 */
export function PendingAnswer({ meta }) {
  const [language, setLanguage] = useState(getCurrentLanguage());
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const unsubscribe = useLanguageStore.subscribe((state) =>
      setLanguage(state.currentLanguage)
    );
    return unsubscribe;
  }, []);

  // Anchor the count on the turn's start.
  //
  // `startedAt` comes from the transport's own clock and `elapsedMs` from the
  // server's. Preferring the server-derived anchor when both are present keeps the
  // display honest if the tab was throttled in the background, which browsers do
  // aggressively to timers in hidden tabs.
  const anchor = useMemo(() => {
    const startedAt = meta && typeof meta.startedAt === 'number' ? meta.startedAt : null;
    const elapsedMs = meta && typeof meta.elapsedMs === 'number' ? meta.elapsedMs : null;
    if (startedAt !== null && elapsedMs !== null && elapsedMs > 0) {
      return Date.now() - elapsedMs;
    }
    return startedAt !== null ? startedAt : Date.now();
    // Recomputed whenever a new heartbeat lands, which is the correction.
  }, [meta && meta.startedAt, meta && meta.elapsedMs]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const seconds = Math.max(0, Math.floor((now - anchor) / 1000));

  return (
    <span className="chat-pending" role="status" aria-live="polite">
      <span className="chat-pending__label">{t('chat_thinking_2610', language)}</span>
      {/* `tabular-nums` in the stylesheet stops the bubble twitching as digits
          change width. */}
      <span className="chat-pending__elapsed">{formatElapsed(seconds)}</span>
    </span>
  );
}

/** `m:ss` past a minute, plain seconds below it. */
function formatElapsed(seconds) {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}:${String(rest).padStart(2, '0')}`;
}

export { formatElapsed };
export default PendingAnswer;
